<template>
  <div class="online-study-details">
    <breadcrumb></breadcrumb>
    <div class="flex-center">
      <div class="content">
        <div class="title">{{ courseInfo.trainingTitle || "" }}</div>
        <div class="time" v-if="courseInfo.createTime">
          发布时间：{{ courseInfo.createTime }}
        </div>
        <div v-if="courseInfo.trainingType == '网络'" class="video-container">
          <div
              class="video-wrapper"
              v-if="courseInfo.studyContent && courseInfo.studyType == '视频'"
          >
            <!-- 视频封面，点击后才加载 -->
            <div v-if="showPoster" class="video-poster" @click="loadVideo">
              <div class="play-btn">▶</div>
              <div class="poster-text">点击播放视频</div>
            </div>

            <video
                v-show="!showPoster"
                ref="videoRef"
                :src="videoSrc"
                preload="none"
                controls
                width="100%"
                controlslist="nofullscreen nodownload noremoteplayback noplaybackrate"
                :disablePictureInPicture="true"
                @play="onVideoPlay"
                @pause="onVideoPause"
                @ended="onVideoEnded"
                @loadedmetadata="onVideoLoadedMetadata"
                @error="onVideoError"
            ></video>

            <!-- 底层：平铺机构名称 -->
            <div class="bg-watermark-layer" ref="bgWatermarkLayer">
              <div
                  v-for="index in bgWatermarkCount"
                  :key="index"
                  class="bg-watermark-item"
              >
                攀枝花市东区社会工作协会
              </div>
            </div>
            <!-- 浮动层：脱敏姓名+电话移动 -->
            <div class="floating-watermark" ref="floatingWatermark">
              {{ watermarkText }}
            </div>
          </div>
          <div v-else v-html="courseInfo.studyType || ''"></div>
        </div>
        <div v-else v-html="courseInfo.content || ''"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { onlineStudy, recordStudyTime } from "@/api/course.js";
import breadcrumb from "@/components/breadcrumb.vue";
import { getUserInfo } from "@/utils/auth";

export default {
  components: {
    breadcrumb,
  },
  data() {
    return {
      loading: false,
      courseInfo: {
        trainingTitle: "",
        trainingName: "",
        trainingType: "",
        beginTime: "",
        endTime: "",
        classHours: "",
        unitPrice: "",
        trainingContent: "",
        videoUrl: "",
        files: [],
      },

      // 视频相关
      startTime: null,
      timer: null,
      studyTimer: null,
      trainingId: null,
      hasRecorded: false,
      isPlaying: false,
      accumulatedTime: 0,
      videoWidth: 0,
      videoHeight: 0,
      userInfo: null,

      // 新增：防止重复加载的标志（核心修复）
      videoLoaded: false,      // 视频是否已加载过
      videoLoading: false,     // 视频是否正在加载
      videoInitiated: false,   // 视频是否已初始化
      showPoster: true,        // 是否显示封面（点击后才加载）
      videoSrc: "",            // 视频地址，只设置一次

      // 背景平铺水印配置
      bgWatermarkCount: 0,
      bgItemWidth: 280,
      bgItemHeight: 120,

      // 浮动水印配置 - 优化后使用 transform
      floatingX: 0,
      floatingY: 0,
      vx: 1.2,
      vy: 1.2,
      floatingWidth: 0,
      floatingHeight: 0,
      wrapperWidth: 0,
      wrapperHeight: 0,
      animationId: null,
      resizeObserver: null,
      skipFrame: false,  // 用于帧节流

      // 重试相关
      videoLoadRetryCount: 0,
      maxVideoRetry: 3,

      // 防抖定时器
      resizeTimer: null,
      recordTimer: null,

      // 请求去重
      pendingRecordRequest: null,
    };
  },
  computed: {
    desensitizedName() {
      const name = this.userInfo?.user?.nickName || this.userInfo?.nickName;
      if (!name) return "";
      if (/[\u4e00-\u9fa5]/.test(name)) {
        const firstChar = name.charAt(0);
        const restLength = name.length - 1;
        if (restLength <= 0) return firstChar;
        return firstChar + "*".repeat(restLength);
      }
      const firstChar = name.charAt(0);
      const restLength = name.length - 1;
      if (restLength <= 0) return firstChar;
      return firstChar + "*".repeat(restLength);
    },
    desensitizedPhone() {
      let phone = this.userInfo?.user?.phonenumber || this.userInfo?.phonenumber;
      if (!phone) return "";
      phone = phone.toString().trim();
      if (phone.length === 11) {
        return phone.slice(0, 3) + "****" + phone.slice(7);
      }
      if (phone.length >= 7) {
        return phone.slice(0, 3) + "****" + phone.slice(-4);
      }
      return "****";
    },
    watermarkText() {
      const namePart = this.desensitizedName;
      const phonePart = this.desensitizedPhone;
      if (namePart && phonePart) return `${namePart} ${phonePart}`;
      if (namePart) return namePart;
      if (phonePart) return phonePart;
      return "攀枝花";
    },
  },
  created() {
    this.getStudyDetails();
    this.userInfo = getUserInfo();
    window.addEventListener("beforeunload", this.recordStudyDuration);
  },
  mounted() {
    window.addEventListener("resize", this.handleResizeDebounced);

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        const wrapper = this.$el.querySelector(".video-wrapper");
        if (wrapper && this.videoInitiated) {
          this.initBgWatermark();
          this.updateFloatingBounds();
        }
      });
      this.resizeObserver.observe(this.$el);
    }
  },
  beforeDestroy() {
    this.stopTimer();
    this.stopStudyTimer();
    this.recordStudyDuration();
    this.stopFloatingAnimation();

    window.removeEventListener("beforeunload", this.recordStudyDuration);
    window.removeEventListener("resize", this.handleResizeDebounced);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    if (this.recordTimer) {
      clearTimeout(this.recordTimer);
    }
  },
  methods: {
    async getStudyDetails() {
      this.loading = true;
      this.trainingId = this.$route.query.trainingId;

      try {
        const res = await onlineStudy({ trainingId: this.trainingId });
        this.loading = false;

        if (res.data.code === 200 && res.data.data) {
          this.courseInfo = {
            ...res.data.data.trainingDetail,
            trainingStudyProgress: res.data.data.trainingStudyProgress,
          };

          // 核心修复：只设置一次视频地址，不触发重新渲染
          if (this.courseInfo.studyContent && this.courseInfo.studyType == "视频") {
            this.videoSrc = this.courseInfo.studyContent;
          }

          if (this.courseInfo.trainingType == "网络") {
            this.startTimer();
            this.startStudyTimer();
          }

          this.$nextTick(() => {
            this.initBgWatermark();
          });
        } else {
          this.$message.error(res.data.msg || "获取详情失败");
        }
      } catch (err) {
        this.loading = false;
        this.$message.error("获取详情失败");
        console.error("获取课程详情失败:", err);
      }
    },

    // 用户点击播放按钮才加载视频
    loadVideo() {
      if (this.videoLoading || this.videoLoaded) return;

      this.videoLoading = true;
      this.showPoster = false;

      this.$nextTick(() => {
        // 延迟初始化水印，确保DOM已渲染
        setTimeout(() => {
          if (!this.videoInitiated) {
            this.initFloatingWatermark();
            this.videoInitiated = true;
          }
        }, 100);

        const video = this.$refs.videoRef;
        if (video) {
          video.load();
        }
      });
    },

    // 初始化背景平铺水印（网格布局）
    initBgWatermark() {
      const wrapper = this.$el.querySelector(".video-wrapper");
      const layer = this.$refs.bgWatermarkLayer;
      if (!wrapper || !layer) return;

      const w = wrapper.clientWidth;
      const h = wrapper.clientHeight;
      if (w === 0 || h === 0) return;

      const itemWidth = 420;
      const itemHeight = 200;
      const cols = Math.ceil(w / itemWidth);
      const rows = Math.ceil(h / itemHeight);
      this.bgWatermarkCount = cols * rows;

      layer.style.gridTemplateColumns = `repeat(${cols}, ${itemWidth}px)`;
      layer.style.gridTemplateRows = `repeat(${rows}, ${itemHeight}px)`;
      layer.style.justifyContent = "center";
      layer.style.alignContent = "center";
      layer.style.gap = "24px";
    },

    // 只更新浮动水印边界
    updateFloatingBounds() {
      const wrapper = this.$el.querySelector(".video-wrapper");
      const floating = this.$refs.floatingWatermark;
      if (!wrapper || !floating) return;

      const newWrapperWidth = wrapper.clientWidth;
      const newWrapperHeight = wrapper.clientHeight;
      if (newWrapperWidth === 0 || newWrapperHeight === 0) return;

      this.wrapperWidth = newWrapperWidth;
      this.wrapperHeight = newWrapperHeight;
      this.floatingWidth = floating.offsetWidth;
      this.floatingHeight = floating.offsetHeight;

      let newX = this.floatingX;
      let newY = this.floatingY;

      if (newX + this.floatingWidth > this.wrapperWidth) {
        newX = this.wrapperWidth - this.floatingWidth;
      }
      if (newY + this.floatingHeight > this.wrapperHeight) {
        newY = this.wrapperHeight - this.floatingHeight;
      }
      if (newX < 0) newX = 0;
      if (newY < 0) newY = 0;

      this.floatingX = newX;
      this.floatingY = newY;
    },

    // 初始化浮动水印
    initFloatingWatermark() {
      const wrapper = this.$el.querySelector(".video-wrapper");
      const floating = this.$refs.floatingWatermark;
      if (!wrapper || !floating) return;

      this.wrapperWidth = wrapper.clientWidth;
      this.wrapperHeight = wrapper.clientHeight;
      if (this.wrapperWidth === 0 || this.wrapperHeight === 0) return;

      this.floatingWidth = floating.offsetWidth;
      this.floatingHeight = floating.offsetHeight;

      if (this.floatingWidth === 0 || this.floatingHeight === 0) {
        setTimeout(() => {
          this.initFloatingWatermark();
        }, 50);
        return;
      }

      const maxX = this.wrapperWidth - this.floatingWidth;
      const maxY = this.wrapperHeight - this.floatingHeight;
      this.floatingX = Math.max(0, Math.min(maxX, Math.random() * maxX));
      this.floatingY = Math.max(0, Math.min(maxY, Math.random() * maxY));

      const speed = 0.6 + Math.random() * 0.9;
      this.vx = Math.random() > 0.5 ? speed : -speed;
      this.vy = Math.random() > 0.5 ? speed : -speed;

      this.startFloatingAnimation();
    },

    startFloatingAnimation() {
      if (this.animationId) cancelAnimationFrame(this.animationId);
      const step = () => {
        this.moveFloatingWatermark();
        this.animationId = requestAnimationFrame(step);
      };
      this.animationId = requestAnimationFrame(step);
    },

    // 优化：使用 transform 和帧节流
    moveFloatingWatermark() {
      // 帧节流：每隔一帧才更新，减少重绘
      if (this.skipFrame) {
        this.skipFrame = false;
        return;
      }
      this.skipFrame = true;

      const floatingEl = this.$refs.floatingWatermark;
      if (!floatingEl) return;

      const wrapper = this.$el.querySelector(".video-wrapper");
      if (wrapper) {
        this.wrapperWidth = wrapper.clientWidth;
        this.wrapperHeight = wrapper.clientHeight;
      }

      this.floatingWidth = floatingEl.offsetWidth;
      this.floatingHeight = floatingEl.offsetHeight;

      if (this.wrapperWidth === 0 || this.wrapperHeight === 0) return;

      let newX = this.floatingX + this.vx;
      let newY = this.floatingY + this.vy;

      if (newX <= 0) {
        newX = 0;
        this.vx = -this.vx;
        this.vx = this.vx * (0.9 + Math.random() * 0.2);
      } else if (newX + this.floatingWidth >= this.wrapperWidth) {
        newX = this.wrapperWidth - this.floatingWidth;
        this.vx = -this.vx;
        this.vx = this.vx * (0.9 + Math.random() * 0.2);
      }

      if (newY <= 0) {
        newY = 0;
        this.vy = -this.vy;
        this.vy = this.vy * (0.9 + Math.random() * 0.2);
      } else if (newY + this.floatingHeight >= this.wrapperHeight) {
        newY = this.wrapperHeight - this.floatingHeight;
        this.vy = -this.vy;
        this.vy = this.vy * (0.9 + Math.random() * 0.2);
      }

      const minSpeed = 0.4;
      const maxSpeed = 2.0;
      this.vx = Math.min(maxSpeed, Math.max(minSpeed, Math.abs(this.vx))) * Math.sign(this.vx);
      this.vy = Math.min(maxSpeed, Math.max(minSpeed, Math.abs(this.vy))) * Math.sign(this.vy);

      this.floatingX = newX;
      this.floatingY = newY;

      // 使用 transform 替代 left/top，性能更好
      floatingEl.style.transform = `translate3d(${this.floatingX}px, ${this.floatingY}px, 0)`;
    },

    stopFloatingAnimation() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
    },

    onVideoPlay() {
      if (!this.isPlaying) {
        this.isPlaying = true;
        this.startTime = Date.now();
      }
    },

    onVideoPause() {
      if (this.isPlaying) {
        this.isPlaying = false;
        const endTime = Date.now();
        const seconds = (endTime - this.startTime) / 1000;
        this.accumulatedTime += seconds;
      }
    },

    onVideoEnded() {
      if (this.isPlaying) {
        this.isPlaying = false;
        const endTime = Date.now();
        const seconds = (endTime - this.startTime) / 1000;
        this.accumulatedTime += seconds;
      }
      this.recordStudyDuration();
    },

    // 核心修复：防止重复处理loadedmetadata
    onVideoLoadedMetadata() {
      if (this.videoLoaded) {
        console.log("视频已加载过，跳过重复处理");
        return;
      }

      this.videoLoaded = true;
      this.videoLoading = false;
      this.updateVideoRenderSize();
      this.videoLoadRetryCount = 0;

      // 只在首次加载时初始化水印
      if (!this.videoInitiated) {
        this.initBgWatermark();
        this.updateFloatingBounds();
      }
    },

    onVideoError(event) {
      console.error("视频加载错误:", event);
      this.videoLoading = false;

      if (this.videoLoadRetryCount < this.maxVideoRetry) {
        this.videoLoadRetryCount++;
        console.log(`视频加载失败，第 ${this.videoLoadRetryCount} 次重试...`);
        setTimeout(() => {
          if (this.$refs.videoRef && !this.videoLoaded) {
            this.$refs.videoRef.load();
          }
        }, 1000);
      } else {
        this.$message.error("视频加载失败，请刷新页面重试");
        this.showPoster = true;
      }
    },

    startTimer() {
      this.startTime = Date.now();
    },

    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    startStudyTimer() {
      this.stopStudyTimer();
      this.studyTimer = setInterval(() => {
        this.recordStudyDuration();
      }, 30000); // 改为30秒上报一次
    },

    stopStudyTimer() {
      if (this.studyTimer) {
        clearInterval(this.studyTimer);
        this.studyTimer = null;
      }
    },

    // 核心修复：请求去重
    async recordStudyDuration() {
      if (this.recordTimer) {
        clearTimeout(this.recordTimer);
      }

      this.recordTimer = setTimeout(async () => {
        if (!this.trainingId || this.courseInfo.trainingType != "网络") {
          return;
        }

        let totalSeconds = 0;
        if (this.courseInfo.studyType == "视频") {
          totalSeconds = this.accumulatedTime;
          if (this.isPlaying && this.startTime) {
            const endTime = Date.now();
            const currentSessionSeconds = (endTime - this.startTime) / 1000;
            totalSeconds += currentSessionSeconds;
          }
        } else {
          if (this.startTime) {
            const endTime = Date.now();
            totalSeconds = (endTime - this.startTime) / 1000;
          }
        }

        const duration = Math.floor(totalSeconds / 60);
        if (duration > 0) {
          const params = {
            trainingId: this.trainingId,
            progress: duration,
            status: 2,
          };

          // 请求去重
          const key = JSON.stringify(params);

          try {
            const res = await recordStudyTime(params);
            if (res.data.code === 200) {
              console.log("学习进度记录成功:", duration, "分钟");
            }
          } catch (err) {
            console.error("学习进度记录失败:", err);
          }
        }
      }, 500);
    },

    updateVideoRenderSize() {
      const video = this.$refs.videoRef;
      if (video) {
        this.videoWidth = video.clientWidth;
        this.videoHeight = video.clientHeight;
      }
    },

    handleResizeDebounced() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        if (this.videoInitiated) {
          this.initBgWatermark();
          this.updateFloatingBounds();
        }
      }, 150);
    },
  },
};
</script>

<style lang="less" scoped>
.online-study-details {
  padding: 0 10%;
  padding-bottom: 109px;
  padding-top: 26px;

  .content {
    width: 90%;
    background: #fff6e8;
    padding: 64px;
    margin-top: 48px;
    font-size: 20px;
    color: #333333;

    .title {
      font-size: 28px;
      color: #ed9d26;
      font-weight: bold;
      text-align: center;
    }

    .time {
      font-size: 20px;
      color: #333333;
      margin: 32px 0;
      text-align: center;
    }

    .video-container {
      margin: 32px 0;

      video {
        display: block;
        border-radius: 8px;
        background: #000;
      }
    }

    .video-wrapper {
      position: relative;
      overflow: hidden;
    }

    // 视频封面
    .video-poster {
      position: relative;
      width: 100%;
      min-height: 400px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      border-radius: 8px;

      .play-btn {
        width: 80px;
        height: 80px;
        background: rgba(255, 255, 255, 0.9);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 40px;
        color: #667eea;
        transition: transform 0.3s;
      }

      .poster-text {
        margin-top: 20px;
        color: white;
        font-size: 18px;
      }

      &:hover .play-btn {
        transform: scale(1.1);
      }
    }

    // 背景平铺水印层
    .bg-watermark-layer {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: grid;
      pointer-events: none;
      z-index: 5;
    }

    .bg-watermark-item {
      display: flex;
      align-items: center;
      justify-content: center;
      color: rgba(255, 255, 255, 0.35);
      font-size: 20px;
      font-weight: bold;
      transform: rotate(-30deg);
      white-space: nowrap;
      user-select: none;
    }

    // 浮动水印 - 使用 transform 优化性能
    .floating-watermark {
      position: absolute;
      padding: 8px 16px;
      color: rgba(255, 255, 255, 0.9);
      font-size: 18px;
      font-weight: bold;
      border-radius: 8px;
      white-space: nowrap;
      pointer-events: none;
      z-index: 20;
      backdrop-filter: blur(2px);
      will-change: transform;
      top: 0;
      left: 0;
    }

    ::v-deep {
      img {
        max-width: 100% !important;
        height: auto !important;
        display: block;
        margin: 10px auto;
      }
      table {
        max-width: 100% !important;
        border-collapse: collapse;
        margin: 10px 0;
        overflow-x: auto;
        display: block;
      }
      table td,
      table th {
        border: 1px solid #ddd;
        padding: 8px;
        word-wrap: break-word;
        word-break: break-all;
      }
      p,
      div,
      span {
        max-width: 100%;
        word-wrap: break-word;
        word-break: break-word;
      }
      video,
      iframe {
        max-width: 100% !important;
        height: auto !important;
      }
      pre {
        max-width: 100%;
        overflow-x: auto;
        word-wrap: break-word;
        word-break: break-all;
      }
      * {
        box-sizing: border-box;
      }
    }
  }
}

::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner,
::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner a,
::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner a:hover,
::v-deep .el-breadcrumb__item:last-child .el-breadcrumb__inner:hover {
  color: #303133;
  font-weight: 700;
}

::v-deep .el-breadcrumb__inner a,
::v-deep .el-breadcrumb__inner.is-link {
  color: #606266;
  font-weight: 400;
}

video::-webkit-media-controls-timeline {
  display: none;
}
</style>